import React, { useCallback, useEffect, useState } from "react";
import ConfigurablePopup from "components/ConfigurablePopup";
import {
  POPUP_REQUEST_EVENT,
  canShowPopup,
  loadPopup,
  markPopupShown,
} from "utils/popups";
import { bindSignupAbandonmentUnload } from "utils/signupAbandonment";

/**
 * Single mount point for the admin-configured pop-ups that are not owned by a
 * screen of their own (registration abandonment today, post-login bonus reminder
 * once its trigger is activated). Trigger code anywhere in the app asks for a
 * pop-up through the shared bus; this host resolves the config, applies the
 * frequency cap, and renders exactly one at a time.
 *
 * A pop-up that fails its cap never reaches the renderer.
 */
export default function PopupHost({ setModalSignUpPropsData, setModalLoginPropsData }) {
  const [active, setActive] = useState(null);

  useEffect(() => bindSignupAbandonmentUnload(), []);

  useEffect(() => {
    let alive = true;
    const onRequest = async (event) => {
      const type = event?.detail?.type;
      if (!type) return;
      const popup = await loadPopup(type);
      if (!alive || !popup) return;
      if (!canShowPopup(popup)) return;
      markPopupShown(popup);
      // One at a time: a second trigger never stacks on an open pop-up.
      setActive((current) => current || popup);
    };
    window.addEventListener(POPUP_REQUEST_EVENT, onRequest);
    return () => {
      alive = false;
      window.removeEventListener(POPUP_REQUEST_EVENT, onRequest);
    };
  }, []);

  // The consumer tests `props.modalSignUpPropsData == true` (LoginReg), so the
  // auth modal only opens on a boolean pulse — the same one Header sends. Going
  // through it (instead of routing) is what makes the CTA work in the PWA too,
  // where the /register route does not open the modal.
  const pulse = useCallback((setter) => {
    if (!setter) return false;
    setter(true);
    setTimeout(() => setter(false), 500);
    return true;
  }, []);

  const handleAction = useCallback(
    (url) => {
      if (url === "/register") return pulse(setModalSignUpPropsData);
      if (url === "/login") return pulse(setModalLoginPropsData);
      return false;
    },
    [pulse, setModalSignUpPropsData, setModalLoginPropsData]
  );

  return (
    <ConfigurablePopup
      popup={active}
      show={Boolean(active)}
      onClose={() => setActive(null)}
      onAction={handleAction}
    />
  );
}
